import {useState, useCallback} from '@wordpress/element';
import {registerPaymentMethod} from '@woocommerce/blocks-registry';
import {StoreNotice} from '@woocommerce/blocks-checkout';
import {getSetting} from '@woocommerce/settings';
import {Elements, PaymentElement} from "@stripe/react-stripe-js";
import {PaymentMethod, PaymentMethodLabel} from "@wc-stripe/blocks-components";
import {initStripe as loadStripe} from '@wc-stripe/blocks-utils';
import SavedCardComponent from "../saved-card-component";
import {useProcessCheckoutError, useCreatePaymentMethod, useProcessCheckoutSuccess, useElementOptions} from "../hooks";

const data = getSetting('stripe_upm_data');

const PaymentMethodContent = (
    {
        billing,
        emitResponse,
        shouldSavePayment,
        eventRegistration,
        activePaymentMethod
    }) => {
    const name = data.name;
    const gatewayId = data.gatewayId;
    const [loadError, setLoadError] = useState(false);
    const [shouldCreatePaymentMethod, setShouldCreatePaymentMethod] = useState(true);
    const {billingAddress} = billing;
    const {onCheckoutSuccess, onCheckoutFail} = eventRegistration;
    const {noticeContexts} = emitResponse;

    const {
        setPaymentMethodType,
        clearPaymentMethodData
    } = useCreatePaymentMethod({
        name,
        gatewayId,
        emitResponse,
        billingAddress,
        shouldSavePayment,
        eventRegistration,
        activePaymentMethod,
        shouldCreatePaymentMethod
    });

    useProcessCheckoutSuccess({
        name,
        emitResponse,
        billingAddress,
        onCheckoutSuccess,
        activePaymentMethod
    });

    useProcessCheckoutError({
        emitResponse,
        subscriber: onCheckoutFail,
        messageContext: noticeContexts.PAYMENTS,
        clearPaymentMethodData
    });


    const onLoadError = useCallback(response => {
        const {error} = response;
        setLoadError(error);
    }, []);

    const onPaymentElementChange = useCallback((event) => {
        const {value = null, complete = false} = event;
        if (value?.type) {
            setPaymentMethodType(value.type);
            setShouldCreatePaymentMethod(['card', 'link', 'apple_pay', 'google_pay'].includes(value.type));
        }
    }, []);

    const options = {
        defaultValues: {
            billingDetails: {
                phone: billingAddress.phone,
                email: billingAddress.email,
                name: `${billingAddress.first_name} ${billingAddress.last_name}`,
            }
        },
        fields: {
            billingDetails: {
                address: 'never',
                name: 'never',
                email: 'never',
                phone: billingAddress?.phone ? 'never' : 'auto'
            }
        },
        wallets: {applePay: 'never', googlePay: 'never'},
        ...data.paymentElementOptions
    }

    if (loadError) {
        const message = getSetting('stripeErrorMessages').payment_unavailable;
        return (
            <StoreNotice
                status={'warning'}
                isDismissible={false}>
                {message.replace('%s', loadError.message)}
            </StoreNotice>
        )
    }

    return (
        <>
            <PaymentElement
                options={options}
                onChange={onPaymentElementChange}
                onLoadError={onLoadError}/>
        </>
    )
}

const UniversalPaymentMethod = ({data, ...props}) => {
    const {billing, cartData, shouldSavePayment} = props;
    const {extensions} = cartData;
    const {cartTotal, currency} = billing;

    const options = useElementOptions({
        gatewayId: data.gatewayId,
        cartTotal,
        currency,
        elementOptions: extensions.stripe_upm.elementOptions,
        shouldSavePayment
    });

    return (
        <Elements stripe={loadStripe} options={options}>
            <PaymentMethodContent {...props}/>
        </Elements>
    )
}

registerPaymentMethod({
    name: data.name,
    label: <PaymentMethodLabel
        title={data.title}
        paymentMethod={data.name}
        icons={data.icons}/>,
    ariaLabel: 'Credit Cards',
    canMakePayment: () => true,
    content: <PaymentMethod content={UniversalPaymentMethod} data={data}/>,
    savedTokenComponent: <SavedCardComponent data={data}/>,
    edit: <PaymentMethod content={UniversalPaymentMethod} data={data}/>,
    supports: {
        showSavedCards: data.showSavedCards,
        showSaveOption: data.showSaveOption,
        features: data.features
    }
})